import React, { useEffect, useState } from "react";
import { Check, X, Info } from "lucide-react";
import { motion } from "framer-motion";
import { PageHeader } from "@/components/ui/PageHeader";
import { GlassCard } from "@/components/ui/GlassCard";
import { Skeleton } from "@/components/ui/Skeleton";
import { apiGet } from "@/lib/api";
import pt from "@/lib/i18n";

const RBACMatrixPage = () => {
    const [data, setData] = useState(null);

    useEffect(() => {
        apiGet("/admin/rbac-matrix").then(setData).catch(() => setData({ modules: [], roles: [], matrix: {} }));
    }, []);

    if (!data) {
        return (
            <GlassCard padding="p-6">
                <Skeleton height="320px" />
            </GlassCard>
        );
    }

    return (
        <div data-testid="rbac-page">
            <PageHeader title={pt.admin.rbac.title} subtitle={pt.admin.rbac.subtitle} testId="rbac-header" />
            <GlassCard padding="p-4" className="mb-5">
                <div className="flex items-center gap-2 text-sm font-body text-[color:var(--slate-mid)]">
                    <Info size={15} className="text-[color:var(--copper)]" />
                    <span>{pt.admin.rbac.legendHint}</span>
                </div>
            </GlassCard>

            <GlassCard padding="p-0" testId="rbac-matrix">
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr>
                                <th className="sticky left-0 z-10 text-left px-5 py-4 font-body text-[11px] uppercase tracking-[0.16em] text-[color:var(--slate-light)] font-medium border-b border-[color:var(--glass-border)] bg-[color:var(--bg-elevated)]">
                                    {pt.admin.rbac.module}
                                </th>
                                {data.roles.map((r) => (
                                    <th
                                        key={r.key}
                                        className="text-center px-3 py-4 font-body text-[10px] uppercase tracking-[0.14em] text-[color:var(--slate-light)] font-medium border-b border-[color:var(--glass-border)] min-w-[120px]"
                                    >
                                        <div className="flex flex-col items-center gap-1">
                                            <span className="text-[color:var(--slate-deep)] text-[11px] normal-case tracking-normal font-display">
                                                {r.label}
                                            </span>
                                            <span className="font-mono text-[9px] text-[color:var(--slate-light)]">
                                                {r.key}
                                            </span>
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.modules.map((m, mIdx) => {
                                const moduleLabel = pt.nav?.[m.key] || m.label;
                                return (
                                <motion.tr
                                    key={m.key}
                                    initial={{ opacity: 0, x: -8 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: mIdx * 0.03 }}
                                    className="border-b border-[color:var(--glass-border-subtle)] hover:bg-[color:var(--copper-subtle)]"
                                    data-testid={`rbac-row-${m.key}`}
                                >
                                    <td className="sticky left-0 z-10 px-5 py-3.5 bg-[color:var(--bg-surface)] border-r border-[color:var(--glass-border-subtle)]">
                                        <div className="font-display text-sm text-[color:var(--slate-deep)]">
                                            {moduleLabel}
                                        </div>
                                        <div className="font-mono text-[10px] text-[color:var(--slate-light)]">
                                            {m.key}
                                        </div>
                                    </td>
                                    {data.roles.map((r) => {
                                        const allowed = (data.matrix[m.key] || []).includes(r.key);
                                        return (
                                            <td
                                                key={r.key}
                                                className="px-3 py-3.5 text-center"
                                                data-testid={`rbac-cell-${m.key}-${r.key}`}
                                            >
                                                <div
                                                    className="inline-flex items-center justify-center w-8 h-8 rounded-full"
                                                    style={{
                                                        background: allowed
                                                            ? "var(--copper-subtle)"
                                                            : "rgba(91,107,126,0.08)",
                                                        color: allowed
                                                            ? "var(--copper-dark)"
                                                            : "var(--slate-light)",
                                                    }}
                                                    title={allowed ? pt.admin.rbac.allowed : pt.admin.rbac.denied}
                                                >
                                                    {allowed ? <Check size={15} strokeWidth={2.4} /> : <X size={14} strokeWidth={1.8} />}
                                                </div>
                                            </td>
                                        );
                                    })}
                                </motion.tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </GlassCard>
        </div>
    );
};

export default RBACMatrixPage;
